import type { ChangeEventHandler } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Group from 'components/utils/Group'
import type { AlertChannelSelection } from 'hooks/Alerts/useAlertChannelSelection'

interface Props {
  /**
   * The whole selection from `useAlertChannelSelection`. Passed as one object rather than a dozen
   * props so the load gating and the phone-capture rules cannot be wired up half way.
   */
  selection: AlertChannelSelection
  /**
   * `responsive` stacks the question above the options on mobile and puts them inline from tablet
   * up (the destination watch modal). `inline` keeps them on one row (the price alert card).
   */
  layout?: 'responsive' | 'inline'
  idPrefix?: string
}

/**
 * "How would you like to hear from us?" — the email and SMS delivery choices shown on every alert
 * surface.
 *
 * Both options stay disabled until the account's real channels have loaded, so a checkbox never
 * shows a state the user did not pick — and they stay disabled when that fetch failed, because
 * the defaults would silently rewrite channels the user had already chosen. Choosing SMS on an account with no mobile number reveals a
 * mobile field instead of disabling the option; the number is saved to the account when the
 * surface's own call to action is used.
 */
function AlertChannelChoice({ selection, layout = 'responsive', idPrefix = 'alert-channel' }: Props) {
  const intl = useIntl()
  const { channelsLoaded, email, sms, setEmail, setSms, needsPhoneNumber, phoneNumber, phonePrefix, phonePrefixCountry, setPhoneNumber, setPhonePrefix } = selection

  const handleEmailChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setEmail(e.currentTarget.checked)
  }, [setEmail])

  const handleSmsChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSms(e.currentTarget.checked)
  }, [setSms])

  const handleNumberChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setPhoneNumber(e.currentTarget.value)
  }, [setPhoneNumber])

  const handlePrefixChange = useCallback((data: { phonePrefix: string, code: string }) => {
    setPhonePrefix(data.phonePrefix, data.code)
  }, [setPhonePrefix])

  const contactMethodQuestion = intl.formatMessage({
    id: '73K_60hUXYIb5ovj3Ltu',
    defaultMessage: 'How would you like to hear from us?',
  })

  const emailLabel = intl.formatMessage({
    id: 'yUC827BpmpcqZGE4732d',
    defaultMessage: 'Email',
  })

  const smsLabel = intl.formatMessage({
    id: 'B_ylFhyVcqqTMQNHlmhb',
    defaultMessage: 'SMS',
  })

  const phoneLabel = intl.formatMessage({
    id: 'Kzr4iepO3EHs5PFtRXYR',
    defaultMessage: 'Mobile number',
  })

  return (
    <Group direction="vertical" gap={12}>
      <Group
        direction="vertical"
        tabletDirection={layout === 'responsive' ? 'horizontal' : undefined}
        gap="12 24"
        tabletVerticalAlign="center"
      >
        <BodyText variant="medium" colour="light-foreground-alt">{contactMethodQuestion}</BodyText>
        <Group direction="horizontal" gap={24} verticalAlign="center">
          <CheckboxInput id={`${idPrefix}-email`} checked={email} disabled={!channelsLoaded} onChange={handleEmailChange}>
            {emailLabel}
          </CheckboxInput>
          <CheckboxInput id={`${idPrefix}-sms`} checked={sms} disabled={!channelsLoaded} onChange={handleSmsChange}>
            {smsLabel}
          </CheckboxInput>
        </Group>
      </Group>
      {needsPhoneNumber && <PhoneInput
        id={`${idPrefix}-phone`}
        name={`${idPrefix}-phone`}
        label={phoneLabel}
        phoneNumber={phoneNumber}
        phonePrefix={phonePrefix}
        prefixCountry={phonePrefixCountry}
        onNumberChange={handleNumberChange}
        onPrefixChange={handlePrefixChange}
        required
        useStrictValidation
      />}
    </Group>
  )
}

export default AlertChannelChoice
